<div class="editor-loading-skeleton" *ngIf="loading">
    <app-skeleton-loader *ngFor="let i of [1, 2, 3, 4]" height="48px" borderRadius="8px"></app-skeleton-loader>
</div>

<div class="editor-body" *ngIf="!loading">
    <form [formGroup]="form" *ngIf="form">
        <h3 class="settings-section">{{ 'permit.config.editor.section_general' | translate }}</h3>

        <div class="bilingual-row">
            <app-form-input-v2
                [label]="'permit.config.common.name_fr' | translate"
                placeholder=""
                formControlName="nameFr"
            >
                <app-form-error *ngIf="form.get('nameFr').touched && form.get('nameFr').hasError('required')">
                    {{ 'permit.config.common.required_error' | translate }}
                </app-form-error>
            </app-form-input-v2>

            <app-form-input-v2
                [label]="'permit.config.common.name_en' | translate"
                placeholder=""
                formControlName="nameEn"
            >
                <app-form-error *ngIf="form.get('nameEn').touched && form.get('nameEn').hasError('required')">
                    {{ 'permit.config.common.required_error' | translate }}
                </app-form-error>
            </app-form-input-v2>
        </div>

        <div class="form-grid">
            <app-form-input-v2
                [label]="'permit.config.editor.prefix' | translate"
                placeholder=""
                formControlName="numberPrefix"
            >
                <app-form-error *ngIf="form.get('numberPrefix').touched && form.get('numberPrefix').invalid">
                    {{ 'permit.config.editor.prefix_error' | translate }}
                </app-form-error>
            </app-form-input-v2>

            <app-form-input-v2
                [label]="'permit.config.editor.validity_days' | translate"
                placeholder=""
                type="number"
                formControlName="validityDurationDays"
            ></app-form-input-v2>
        </div>

        <div class="field-row-select">
            <app-form-select-v2
                [label]="'permit.config.editor.department' | translate"
                [placeholder]="'permit.config.editor.department' | translate"
                formControlName="defaultDepartment"
                [options]="departmentOptions"
                [invalid]="form.get('defaultDepartment').touched && form.get('defaultDepartment').invalid"
            >
                <app-form-error *ngIf="form.get('defaultDepartment').touched && form.get('defaultDepartment').hasError('required')">
                    {{ 'permit.config.common.required_error' | translate }}
                </app-form-error>
            </app-form-select-v2>
        </div>

        <div class="statuses-section">
            <span class="field-label">{{ 'permit.config.editor.statuses' | translate }}</span>
            <div class="form-locked-banner" *ngIf="statusesLocked">
                {{ 'permit.config.editor.statuses_locked_hint' | translate }}
            </div>
            <div class="statuses-hint" *ngIf="!statusesLocked">
                {{ 'permit.config.editor.statuses_hint' | translate }}
            </div>
            <div class="status-checkboxes" [class.locked]="statusesLocked">
                <mat-checkbox
                    *ngFor="let status of allStatuses"
                    [checked]="isStatusChecked(status._id)"
                    [disabled]="statusesLocked"
                    (change)="toggleStatus(status._id)"
                >
                    {{ statusName(status) }}
                </mat-checkbox>
            </div>
        </div>

        <div class="applicant-types">
            <span class="field-label">{{ 'permit.config.editor.applicant_types' | translate }}</span>
            <mat-checkbox
                *ngFor="let type of applicantTypeOptions"
                [checked]="applicantTypes.has(type)"
                (change)="toggleApplicantType(type)"
            >
                {{ 'permit.config.editor.applicant_' + type | translate }}
            </mat-checkbox>
        </div>
        <div class="field-error" *ngIf="applicantTypesError">
            {{ 'permit.config.editor.applicant_error' | translate }}
        </div>

        <app-form-toggle [checked]="form.value.active" (change)="form.patchValue({ active: $event }); form.markAsDirty()">
            {{ 'permit.config.common.active' | translate }}
        </app-form-toggle>

        <h3 class="settings-section">{{ 'permit.config.editor.section_form' | translate }}</h3>

        <div class="form-locked-banner" *ngIf="isFormLocked">
            {{ 'permit.config.editor.form_locked_banner' | translate }}
        </div>

        <div class="empty-state" *ngIf="!formFields.length">
            {{ 'permit.config.form_builder.empty' | translate }}
        </div>

        <!-- Editable builder (add / edit / delete / drag) -->
        <ng-container *ngIf="!isFormLocked">
            <div class="field-list" cdkDropList (cdkDropListDropped)="dropField($event)">
                <div class="field-row" *ngFor="let field of formFields" cdkDrag cdkDragLockAxis="y">
                    <span class="drag-handle" cdkDragHandle>⠿</span>
                    <span class="field-row-label">{{ fieldLabel(field) }}</span>
                    <span class="type-chip">{{ 'permit.config.form_builder.type_' + field.fieldType | translate }}</span>
                    <span class="required-badge" *ngIf="field.required">
                        {{ 'permit.config.form_builder.required' | translate }}
                    </span>
                    <ng-container *ngIf="field.fieldType === 'number'">
                        <span class="unit-display" *ngIf="!isEditingUnit(field)">
                            <span class="unit-value" [class.muted]="!unitSummary(field)">{{ unitSummary(field) || '—' }}</span>
                            <button
                                type="button"
                                mat-icon-button
                                class="unit-edit-btn"
                                [attr.aria-label]="'permit.config.form_builder.unit_edit_aria' | translate"
                                (click)="toggleUnitEdit(field)"
                            >
                                <mat-icon>edit</mat-icon>
                            </button>
                        </span>
                        <span class="unit-edit" *ngIf="isEditingUnit(field)">
                            <input
                                class="unit-input"
                                [placeholder]="'permit.config.form_builder.unit_fr' | translate"
                                [ngModel]="unitFr(field)"
                                [ngModelOptions]="{ standalone: true }"
                                (ngModelChange)="onUnitChange(field, 'fr', $event)"
                            />
                            <input
                                class="unit-input"
                                [placeholder]="'permit.config.form_builder.unit_en' | translate"
                                [ngModel]="unitEn(field)"
                                [ngModelOptions]="{ standalone: true }"
                                (ngModelChange)="onUnitChange(field, 'en', $event)"
                            />
                            <button
                                type="button"
                                mat-icon-button
                                class="unit-edit-btn"
                                [attr.aria-label]="'permit.config.form_builder.unit_confirm_aria' | translate"
                                (click)="toggleUnitEdit(field)"
                            >
                                <mat-icon>check</mat-icon>
                            </button>
                        </span>
                    </ng-container>
                    <span class="row-actions">
                        <button type="button" mat-icon-button [attr.aria-label]="'permit.config.form_builder.dialog_title_edit' | translate" (click)="openFieldDialog(field)">
                            <mat-icon>edit</mat-icon>
                        </button>
                        <button type="button" mat-icon-button [attr.aria-label]="'permit.config.form_builder.delete_title' | translate" (click)="removeField(field)">
                            <mat-icon>delete_outline</mat-icon>
                        </button>
                    </span>
                </div>
            </div>
            <button type="button" mat-stroked-button class="add-row-btn" (click)="openFieldDialog()">
                <mat-icon>add</mat-icon>
                {{ 'permit.config.form_builder.add_field' | translate }}
            </button>
        </ng-container>

        <!-- Read-only preview when the form is externally managed. The unit input is the one
             exception the sync lock doesn't apply to (see PermitTypeConfig.fieldUnits). -->
        <div class="field-list readonly" *ngIf="isFormLocked">
            <div class="field-row" *ngFor="let field of formFields">
                <span class="field-row-label">{{ fieldLabel(field) }}</span>
                <span class="type-chip">{{ 'permit.config.form_builder.type_' + field.fieldType | translate }}</span>
                <span class="required-badge" *ngIf="field.required">
                    {{ 'permit.config.form_builder.required' | translate }}
                </span>
                <ng-container *ngIf="field.fieldType === 'number'">
                    <span class="unit-display" *ngIf="!isEditingUnit(field)">
                        <span class="unit-value" [class.muted]="!unitSummary(field)">{{ unitSummary(field) || '—' }}</span>
                        <button
                            type="button"
                            mat-icon-button
                            class="unit-edit-btn"
                            [attr.aria-label]="'permit.config.form_builder.unit_edit_aria' | translate"
                            (click)="toggleUnitEdit(field)"
                        >
                            <mat-icon>edit</mat-icon>
                        </button>
                    </span>
                    <span class="unit-edit" *ngIf="isEditingUnit(field)">
                        <input
                            class="unit-input"
                            [placeholder]="'permit.config.form_builder.unit_fr' | translate"
                            [ngModel]="unitFr(field)"
                            [ngModelOptions]="{ standalone: true }"
                            (ngModelChange)="onUnitChange(field, 'fr', $event)"
                        />
                        <input
                            class="unit-input"
                            [placeholder]="'permit.config.form_builder.unit_en' | translate"
                            [ngModel]="unitEn(field)"
                            [ngModelOptions]="{ standalone: true }"
                            (ngModelChange)="onUnitChange(field, 'en', $event)"
                        />
                        <button
                            type="button"
                            mat-icon-button
                            class="unit-edit-btn"
                            [attr.aria-label]="'permit.config.form_builder.unit_confirm_aria' | translate"
                            (click)="toggleUnitEdit(field)"
                        >
                            <mat-icon>check</mat-icon>
                        </button>
                    </span>
                </ng-container>
                <span class="external-badge" *ngIf="field.externalId">
                    {{ 'permit.config.form_builder.external_badge' | translate }}
                </span>
            </div>
        </div>

        <ng-container *ngIf="typeFiles.length">
            <h3 class="settings-section">{{ 'permit.config.editor.section_files' | translate }}</h3>
            <div class="form-locked-banner">
                {{ 'permit.config.editor.files_locked_banner' | translate }}
            </div>
            <div class="file-list">
                <button
                    type="button"
                    class="file-row"
                    *ngFor="let file of typeFiles"
                    (click)="openTypeFile(file)"
                    [attr.aria-label]="('permit.config.editor.files_open_aria' | translate) + ' ' + typeFileName(file)"
                >
                    <span class="file-icon-wrap">
                        <img *ngIf="isTypeFilePdf(file)" src="assets/images/pdf-file.svg" alt="PDF" class="file-icon" />
                        <img *ngIf="isTypeFileWord(file)" src="assets/images/word-file.svg" alt="Word" class="file-icon" />
                        <img *ngIf="isTypeFileExcel(file)" src="assets/images/sheets-file.svg" alt="Excel" class="file-icon" />
                        <mat-icon *ngIf="!isTypeFilePdf(file) && !isTypeFileWord(file) && !isTypeFileExcel(file)">insert_drive_file</mat-icon>
                    </span>

                    <div class="file-info">
                        <span class="file-name">{{ typeFileName(file) }}</span>
                        <span class="file-description" *ngIf="typeFileDescription(file) as description">{{ description }}</span>
                    </div>
                </button>
            </div>
        </ng-container>

        <h3 class="settings-section">{{ 'permit.config.editor.section_fees' | translate }}</h3>
        <div class="toggle-row">
            <app-form-toggle formControlName="feeEnabled">
                {{ 'permit.config.fees.enabled' | translate }}
            </app-form-toggle>
        </div>
        <div class="fees-detail" *ngIf="form.value.feeEnabled">
            <div class="field-group">
                <span class="field-label">{{ 'permit.config.fees.timing' | translate }}</span>
                <div class="radio-row">
                    <label class="radio-option">
                        <input type="radio" formControlName="feeTiming" value="upfront" />
                        <span class="radio-mark"></span>
                        <span class="radio-text">{{ 'permit.config.fees.timing_upfront' | translate }}</span>
                    </label>
                    <label class="radio-option">
                        <input type="radio" formControlName="feeTiming" value="later" />
                        <span class="radio-mark"></span>
                        <span class="radio-text">{{ 'permit.config.fees.timing_later' | translate }}</span>
                    </label>
                </div>
            </div>
            <div class="form-grid">
                <app-form-select-v2
                    [label]="'permit.config.fees.type' | translate"
                    [placeholder]="'permit.config.fees.type' | translate"
                    formControlName="feeType"
                    [options]="feeTypeOptions"
                ></app-form-select-v2>

                <app-form-input-v2
                    [label]="'permit.config.fees.amount' | translate"
                    placeholder=""
                    type="number"
                    formControlName="feeAmount"
                ></app-form-input-v2>
            </div>
            <div class="field-error" *ngIf="feeAmountError">
                {{ 'permit.config.fees.amount_error' | translate }}
            </div>
            <app-form-input-v2
                *ngIf="isInformationalFee"
                [label]="'permit.config.fees.description' | translate"
                placeholder=""
                formControlName="feeDescription"
                class="full-width"
            ></app-form-input-v2>
        </div>
        <div class="toggle-row">
            <app-form-toggle formControlName="externalPaymentEnabled">
                {{ 'permit.config.external_payment.enabled' | translate }}
            </app-form-toggle>
        </div>
        <div class="fees-detail" *ngIf="form.value.externalPaymentEnabled">
            <app-form-textarea
                [label]="'permit.config.external_payment.message_fr' | translate"
                placeholder=""
                [rows]="3"
                formControlName="externalPaymentMessageFr"
                class="full-width"
            ></app-form-textarea>
            <app-form-textarea
                [label]="'permit.config.external_payment.message_en' | translate"
                placeholder=""
                [rows]="3"
                formControlName="externalPaymentMessageEn"
                class="full-width"
            ></app-form-textarea>
            <div class="field-error" *ngIf="externalPaymentMessageError">
                {{ 'permit.config.external_payment.message_required' | translate }}
            </div>
        </div>

        <h3 class="settings-section">{{ 'permit.config.editor.section_tasks' | translate }}</h3>
        <div class="empty-state" *ngIf="!predefinedTasks.length">
            {{ 'permit.config.tasks.empty' | translate }}
        </div>
        <div class="task-row" *ngFor="let task of predefinedTasks; let i = index">
            <span class="task-title">{{ task }}</span>
            <button type="button" mat-icon-button [attr.aria-label]="'permit.config.tasks.remove_aria' | translate" (click)="removeTask(i)">
                <mat-icon>close</mat-icon>
            </button>
        </div>
        <div class="task-add">
            <input
                class="task-input"
                [(ngModel)]="newTask"
                [ngModelOptions]="{ standalone: true }"
                [placeholder]="'permit.config.tasks.placeholder' | translate"
                (keydown.enter)="addTask(); $event.preventDefault()"
            />
            <button type="button" mat-stroked-button (click)="addTask()">
                {{ 'permit.config.tasks.add' | translate }}
            </button>
        </div>

        <div class="form-actions">
            <button type="button" mat-button class="cancel-button" (click)="requestClose()">
                {{ 'permit.config.common.cancel' | translate }}
            </button>
            <button type="button" mat-flat-button class="save-button" [disabled]="saving" (click)="save()">
                {{ 'permit.config.common.save' | translate }}
            </button>
        </div>
    </form>
</div>
